import { useEffect, useRef, useState } from 'react';
import { Filter } from 'lucide-react';
import { Button } from '@/components/ui';

export interface CalendarFiltersDropdownProps {
  hasActiveFilters: boolean;
  children: React.ReactNode;
  /** Accessible name for the filter panel (default: Filter lessons). */
  panelLabel?: string;
}

/** Non-Radix filters menu — avoids React 19 ref loops on calendar mount. */
export function CalendarFiltersDropdown({
  hasActiveFilters,
  children,
  panelLabel = 'Filter lessons',
}: CalendarFiltersDropdownProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      const el = containerRef.current;
      if (el && !el.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div className="relative" ref={containerRef}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="relative inline-flex h-8 w-8 shrink-0 items-center justify-center p-0"
        aria-label="Filters"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Filter className="h-4 w-4 shrink-0" aria-hidden />
        {hasActiveFilters && (
          <span
            className="absolute right-0.5 top-0.5 h-2 w-2 rounded-full bg-primary-600"
            aria-hidden
          />
        )}
      </Button>
      {open ? (
        <div
          className="absolute right-0 top-full z-50 mt-1 w-[min(500px,90vw)] max-h-[80vh] overflow-y-auto rounded-md border border-border bg-popover p-4 text-popover-foreground shadow-md"
          role="dialog"
          aria-label={panelLabel}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}
